"use client"

import { useState, useEffect } from "react"
import Image from "next/image"
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel"

type NewsItem = {
  id: number
  image_name: string
  activity_heading: string
  activity_desc: string
}

interface NewsSectionProps {
  activityType?: 'school' | 'college' | 'trust' | 'all'
  title?: string
  description?: string
}

// Helper function to get image from news data
function getImageFromNews(news: any): string | null {
  // Check for image_name field first, then fallback to other common names
  const imageFields = ['image_name', 'image', 'picture', 'img', 'photo']

  for (const field of imageFields) {
    if (news[field] && typeof news[field] === 'string' && news[field].trim() !== '') {
      console.log(`🖼️ Found news image in field '${field}':`, news[field])
      return news[field]
    }
  }

  console.log('❌ No news image found in any field. Available fields:', Object.keys(news))
  console.log('🖼️ News data:', news)
  return null
}

// Helper function to get heading from news data
function getHeadingFromNews(news: any): string {
  // Check for different possible heading field names
  const headingFields = ['activity_heading', 'activity_header', 'title', 'heading', 'name']

  for (const field of headingFields) {
    if (news[field] && typeof news[field] === 'string' && news[field].trim() !== '') {
      console.log(`📝 Found news heading in field '${field}':`, news[field])
      return news[field]
    }
  }

  console.log('❌ No news heading found. Available fields:', Object.keys(news))
  return 'Untitled News'
}

// Helper function to get description from news data
function getDescFromNews(news: any): string {
  // Check for different possible description field names
  const descFields = ['activity_desc', 'description', 'desc', 'content', 'details']

  for (const field of descFields) {
    if (news[field] && typeof news[field] === 'string' && news[field].trim() !== '') {
      console.log(`📝 Found news description in field '${field}':`, news[field])
      return news[field]
    }
  }

  console.log('❌ No news description found. Available fields:', Object.keys(news))
  return 'No description available'
}

// Component to handle different news image URL patterns
function NewsImage({ imageName, alt, newsData }: { imageName: string, alt: string, newsData: any }) {
  const [currentUrlIndex, setCurrentUrlIndex] = useState(0)

  // Try different possible URL patterns (same as gallery)
  const possibleUrls = [
    `https://dextro-online.com/stjerome_school/web_admin/${imageName}`,
    // `https://dextro-online.com/stjerome_school/API/uploads/${imageName}`,
    // `https://dextro-online.com/stjerome_school/upload/${imageName}`,
    // `https://dextro-online.com/stjerome_school/uploads/${imageName}`,
    imageName.startsWith('http') ? imageName : null, // In case it's already a full URL
  ].filter(Boolean) as string[]

  const handleImageError = () => {
    console.error('❌ News image failed to load:', possibleUrls[currentUrlIndex])
    console.log('🖼️ News data:', newsData)
    console.log('🖼️ Image name from API:', imageName)

    if (currentUrlIndex < possibleUrls.length - 1) {
      console.log('🔄 Trying next news URL pattern:', possibleUrls[currentUrlIndex + 1])
      setCurrentUrlIndex(currentUrlIndex + 1)
    } else {
      console.error('❌ All news image URL patterns failed for:', imageName)
    }
  }

  const handleImageLoad = () => {
    console.log('✅ News image loaded successfully:', possibleUrls[currentUrlIndex])
  }

  if (currentUrlIndex >= possibleUrls.length) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 bg-gray-100">
        <span className="text-sm">No Image</span>
      </div>
    )
  }

  return (
    <Image
      src={possibleUrls[currentUrlIndex]}
      alt={alt}
      fill
      className="object-cover"
      onError={handleImageError}
      onLoad={handleImageLoad}
    />
  )
}

export function NewsSection({
  activityType = 'all',
  title = "Latest from St. Jerome",
  description = "Stay updated with all the exciting news, activities, and announcements from our school community."
}: NewsSectionProps) {
  const [newsItems, setNewsItems] = useState<NewsItem[]>([])
  const [loading, setLoading] = useState(true)

  // Fetch news from API
  useEffect(() => {
    const fetchNews = async () => {
      try {
        const response = await fetch('https://dextro-online.com/stjerome_school/API/get_latest_news.php?action=get_news')

        console.log('📰 Fetching news from:', response.url)
        console.log('📰 Response status:', response.status)

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()
        console.log('📰 News API Response:', data)

        if (data.success && Array.isArray(data.news)) {
          console.log('✅ News loaded:', data.news.length, 'items')
          if (data.news.length > 0) {
            console.log('🖼️ Sample news item:', data.news[0])
            console.log('🖼️ Available fields in news:', Object.keys(data.news[0] || {}))
            console.log('🖼️ image_name field:', data.news[0]?.image_name)
            console.log('🖼️ activity_heading field:', data.news[0]?.activity_heading)
            console.log('🖼️ activity_desc field:', data.news[0]?.activity_desc)
          }
          setNewsItems(data.news)
        } else if (Array.isArray(data)) {
          console.log('✅ News loaded (direct array):', data.length, 'items')
          if (data.length > 0) {
            console.log('🖼️ Sample news item:', data[0])
            console.log('🖼️ Available fields in news:', Object.keys(data[0] || {}))
            console.log('🖼️ image_name field:', data[0]?.image_name)
            console.log('🖼️ activity_heading field:', data[0]?.activity_heading)
            console.log('🖼️ activity_desc field:', data[0]?.activity_desc)
          }
          setNewsItems(data)
        } else {
          console.log('ℹ️ No news data available')
          setNewsItems([])
        }
      } catch (err) {
        console.error('❌ Error fetching news:', err)
        // Don't show error, just show empty state
        setNewsItems([])
      } finally {
        setLoading(false)
      }
    }

    fetchNews()
  }, [activityType])

  // Convert news items to display format
  const items = newsItems.map(news => ({
    id: news.id,
    title: getHeadingFromNews(news),
    desc: getDescFromNews(news),
    imageField: getImageFromNews(news),
    newsData: news
  }))

  return (
    <section id="news" className="py-12 sm:py-16 lg:py-20 bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 sm:mb-12">
          <span className="inline-block px-4 sm:px-6 py-2 sm:py-3 bg-primary/10 text-primary rounded-full text-xs sm:text-sm font-semibold mb-4 border border-primary/20">
            News & Updates
          </span>
          <h3 className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">
            {title}
          </h3>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {description}
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        )}



        {/* Activities Carousel */}
        {!loading && items.length > 0 && (
          <div className="relative">
            <Carousel
              opts={{ align: "start", loop: true }}
              className="w-full"
            >
              <CarouselContent className="-ml-2 md:-ml-4">
                {items.map((item) => (
                  <CarouselItem
                    key={item.id}
                    className="pl-2 md:pl-4 basis-full sm:basis-1/2 lg:basis-1/3"
                  >
                    <article className="h-full bg-card rounded-2xl shadow-lg border border-border overflow-hidden flex flex-col">
                      <div className="relative aspect-[16/9] bg-gray-100">
                        {item.imageField ? (
                          <NewsImage
                            imageName={item.imageField}
                            alt={item.title}
                            newsData={item.newsData}
                          />
                        ) : (
                          <div className="flex items-center justify-center h-full text-gray-500">
                            <span className="text-sm">No Image Available</span>
                          </div>
                        )}
                      </div>
                      <div className="p-4 sm:p-6 flex-1 flex flex-col">
                        <h4 className="font-serif text-lg sm:text-xl font-bold text-foreground mb-2 line-clamp-2">
                          {item.title}
                        </h4>
                        <p className="text-sm sm:text-base text-muted-foreground line-clamp-3">
                          {item.desc}
                        </p>
                      </div>
                    </article>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="hidden md:flex bg-white/90 hover:bg-white border-primary/30 text-primary shadow" />
              <CarouselNext className="hidden md:flex bg-white/90 hover:bg-white border-primary/30 text-primary shadow" />
            </Carousel>
          </div>
        )}

        {/* Empty State */}
        {!loading && items.length === 0 && (
          <div className="text-center py-16 bg-gray-50 rounded-2xl">
            <div className="text-6xl mb-4">📰</div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">No News Available</h3>
            <p className="text-gray-600">
              There are no news items at the moment. Please check back later for updates.
            </p>
          </div>
        )}
      </div>
    </section>
  )
}
